import { useCallback, useEffect, useMemo, useState } from 'react';

import Icon from './Icon.jsx';
import { VERSES } from '../data/verses.js';
import { playCorrect, playWrong } from '../lib/sfx.js';

/**
 * Verse recall — fill in the missing word.
 *
 * The one drill in the app that tests recall rather than recognition, and the
 * only one whose questions are generated rather than written: a verse, one word
 * removed, and three decoys drawn from other verses in the deck. That means it
 * scales with the verse deck for free — adding a verse adds a question, with no
 * question to write.
 *
 * The blanked word is chosen from the verse's longer, more distinctive words.
 * Blanking "the" would be unanswerable and would teach nothing.
 */

/** Words too common or too structural to be worth blanking. */
const STOPWORDS = new Set([
  'the', 'and', 'for', 'but', 'with', 'that', 'this', 'from', 'they', 'them',
  'you', 'your', 'his', 'her', 'him', 'she', 'has', 'have', 'had', 'was',
  'were', 'will', 'shall', 'unto', 'into', 'upon', 'their', 'there', 'which',
  'who', 'whom', 'what', 'when', 'not', 'all', 'are', 'our', 'out', 'one',
  'may', 'can', 'any', 'own', 'him.', 'also', 'been', 'over', 'than', 'then',
]);

const clean = (word) => word.replace(/[^\p{L}\p{M}’'-]/gu, '');

function hashString(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function seededRandom(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Every word in the deck worth using as a decoy, gathered once. */
const VOCABULARY = (() => {
  const words = new Set();
  for (const verse of VERSES) {
    for (const raw of verse.text.split(/\s+/)) {
      const word = clean(raw);
      if (word.length >= 4 && !STOPWORDS.has(word.toLowerCase())) words.add(word.toLowerCase());
    }
  }
  return [...words];
})();

/**
 * Turn a verse into a question, deterministically — the same verse always
 * blanks the same word, so a player cannot reroll an awkward one by leaving
 * and coming back.
 */
export function buildRecall(verse) {
  const rand = seededRandom(hashString(verse.id));
  const tokens = verse.text.split(/(\s+)/);

  const candidates = [];
  tokens.forEach((token, i) => {
    const word = clean(token);
    if (word.length >= 5 && !STOPWORDS.has(word.toLowerCase())) candidates.push({ i, word });
  });
  if (!candidates.length) return null;

  const target = candidates[Math.floor(rand() * candidates.length)];

  // Decoys of a similar length, so length is not a tell.
  const answer = target.word;
  const pool = VOCABULARY.filter(
    (w) => w !== answer.toLowerCase() && Math.abs(w.length - answer.length) <= 3,
  );
  const decoys = [];
  const guard = new Set([answer.toLowerCase()]);
  while (decoys.length < 3 && pool.length) {
    const pick = pool[Math.floor(rand() * pool.length)];
    if (guard.has(pick)) continue;
    guard.add(pick);
    decoys.push(pick);
  }
  if (decoys.length < 3) return null;

  const options = [answer, ...decoys];
  // Shuffle so the answer is not always first.
  for (let i = options.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [options[i], options[j]] = [options[j], options[i]];
  }

  return {
    id: verse.id,
    ref: verse.ref,
    theme: verse.theme,
    before: tokens.slice(0, target.i).join(''),
    after: tokens.slice(target.i + 1).join(''),
    // Punctuation attached to the blanked token is kept, so the sentence still
    // reads correctly once the answer is filled in.
    trailing: tokens[target.i].slice(clean(tokens[target.i]).length),
    answer,
    options,
    correct: options.indexOf(answer),
  };
}

export default function VerseRecall({ verses, onScore, onExit }) {
  const rounds = useMemo(
    () => verses.map(buildRecall).filter(Boolean).slice(0, 10),
    [verses],
  );

  const [index, setIndex] = useState(0);
  const [choice, setChoice] = useState(null);
  const [right, setRight] = useState(0);

  useEffect(() => {
    setIndex(0);
    setChoice(null);
    setRight(0);
  }, [rounds]);

  const round = rounds[index];
  const answered = choice !== null;
  const finished = index >= rounds.length;

  const choose = useCallback(
    (i) => {
      if (answered || !round) return;
      const correct = i === round.correct;
      setChoice(i);
      if (correct) {
        setRight((n) => n + 1);
        playCorrect();
        onScore?.(8);
      } else {
        playWrong();
      }
    },
    [answered, round, onScore],
  );

  if (!rounds.length) {
    return (
      <section className="screen">
        <div className="empty">
          <p className="empty__icon">
            <Icon name="book" size={44} />
          </p>
          <h3>Nothing to recall yet</h3>
          <p>Unlock more verses and this fills up.</p>
          {onExit ? (
            <button type="button" className="btn btn--primary" onClick={onExit}>
              Back to practice
            </button>
          ) : null}
        </div>
      </section>
    );
  }

  if (finished) {
    return (
      <section className="screen">
        <div className="summary">
          <p className="summary__icon">
            <Icon name={right === rounds.length ? 'radiance' : 'book'} size={44} />
          </p>
          <h3>
            {right} / {rounds.length} recalled
          </h3>
          <p>
            {right === rounds.length
              ? 'Word perfect.'
              : 'Recall is harder than recognition — that is why it works.'}
          </p>
          <div className="summary__actions">
            {onExit ? (
              <button type="button" className="btn btn--primary" onClick={onExit}>
                Back to practice
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setIndex(0);
                setChoice(null);
                setRight(0);
              }}
            >
              Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="screen">
      <div className="run-bar">
        {onExit ? (
          <button type="button" className="icon-btn icon-btn--sm" onClick={onExit}>
            <Icon name="arrow" size={15} className="icon--flip" />
            Practice
          </button>
        ) : (
          <span />
        )}
        <span className="run-bar__count">
          {index + 1} / {rounds.length}
        </span>
      </div>

      <div className="screen__heading">
        <h2>Verse recall</h2>
        <p>Which word completes it?</p>
      </div>

      <div className="recall">
        <blockquote className="recall__text scripture">
          {round.before}
          <span className={`recall__blank${answered ? ' is-filled' : ''}`}>
            {answered ? round.answer : '     '}
          </span>
          {round.trailing}
          {round.after}
        </blockquote>
        <cite className="recall__ref citation">
          {round.ref} · {round.theme}
        </cite>
      </div>

      <ul className="options options--wrap">
        {round.options.map((option, i) => {
          const isCorrect = i === round.correct;
          const isChosen = i === choice;
          let className = 'option option--word';
          if (answered && isCorrect) className += ' is-correct';
          else if (answered && isChosen) className += ' is-wrong';
          else if (answered) className += ' is-dim';

          return (
            <li key={option}>
              <button type="button" className={className} onClick={() => choose(i)} disabled={answered}>
                <span className="option__label">{option}</span>
              </button>
            </li>
          );
        })}
      </ul>

      {answered ? (
        <div className={`explain${choice === round.correct ? ' is-correct' : ' is-wrong'}`}>
          <strong>{choice === round.correct ? 'Correct' : 'Not quite'}</strong>
          <p>
            {round.ref} — “{round.answer}”.
          </p>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              setChoice(null);
              setIndex((i) => i + 1);
            }}
          >
            {index === rounds.length - 1 ? 'See results' : 'Next verse'}
          </button>
        </div>
      ) : null}
    </section>
  );
}
